import { Button, DrawerBody, DrawerHeader, DrawerHeaderTitle, InlineDrawer, mergeClasses, Text, Tooltip } from '@fluentui/react-components';
import { ArrowClockwiseRegular, ChevronDoubleLeft20Filled, ChevronDoubleRight16Filled } from '@fluentui/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { ImperativePanelGroupHandle } from 'react-resizable-panels';

import { STATUS } from '../../../routes/idqa-details/explorer/explorer-utils';
import { ExplorerStatus } from '../../../routes/idqa-details/explorer/ExplorerWrapper';
import commonStyle from '../../../styles/CommonStyles';
import { FluentPanel } from '../reasiable-panel/FluentResizablePanel';
import DQTree, { DQTreeSkeleton, TreeItemType } from '../tree/Tree';
import useStyle from './ExplorerStyles';

type ExplorerProp = {
	status: ExplorerStatus;
	drawerHeaderTitle: string;
	treeTitle?: string;
	defaultOpenTreeBranch?: string[];
	tableData?: TreeItemType;
	onTreeSelectionChange: (value: TreeItemType, parentDetails: TreeItemType) => void;
	loadChildrens: (value: TreeItemType) => Promise<{ childrens: TreeItemType[]; moreElements: boolean } | undefined>;
	loadMoreNodes: (value: TreeItemType) => Promise<{ childrens: TreeItemType[]; moreElements: boolean } | undefined>;
	onExplorerRefresh?: () => void;
	defaultSelectedTableItems?: string;
	enableExpandablePanel?: boolean;
	defaultSize?: number;
	maxSize?: number;
	panelGroupRef?: React.RefObject<ImperativePanelGroupHandle>;
	minSize?: number;
};
export const Explorer: React.FC<ExplorerProp> = ({
	status,
	tableData,
	drawerHeaderTitle,
	treeTitle,
	defaultSelectedTableItems,
	defaultOpenTreeBranch,
	panelGroupRef,
	loadMoreNodes,
	loadChildrens,
	onTreeSelectionChange,
	onExplorerRefresh,
	enableExpandablePanel,
	defaultSize = 15,
	maxSize = 25,
	minSize = 4,
}) => {
	const [open, setOpen] = useState<boolean>(true);
	const style = useStyle();
	const { flexStyles, spacingStyles } = commonStyle;
	const commonFlexStyles = flexStyles();
	const commonSpacingStyles = spacingStyles();
	const treeItems = useMemo(() => tableData && tableData, [tableData]);
	const defaultOpenItems = useMemo(() => defaultOpenTreeBranch && defaultOpenTreeBranch, [defaultOpenTreeBranch]);

	useEffect(() => {
		if (panelGroupRef?.current) {
			// Determine the intended first panel size
			const first = open ? defaultSize : 5;
			// The second panel takes the remaining space
			const second = 100 - first;
			panelGroupRef.current.setLayout([first, second]);
		}
	}, [open]);

	const expandedExplorer = (
		<InlineDrawer size="small" className={style.explorerDimension} open={open}>
			<DrawerHeader>
				<DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Close" icon={<ChevronDoubleLeft20Filled />} onClick={() => setOpen(false)} />}>
					{drawerHeaderTitle}
				</DrawerHeaderTitle>
			</DrawerHeader>
			<DrawerBody>
				<div className={mergeClasses(style.flex, style.marginTop8)}>
					{treeTitle && (
						<Tooltip content={treeTitle} relationship="label">
							<Text className={style.textOverFlowElipsis} size={300} weight="semibold">
								{treeTitle}
							</Text>
						</Tooltip>
					)}
					{onExplorerRefresh && <Button size="small" icon={<ArrowClockwiseRegular />} appearance="transparent" onClick={onExplorerRefresh} />}
				</div>
				{(status.status === STATUS.SUCCESS || status.status === STATUS.SYNC_EXPLORER) && treeItems ? (
					<DQTree
						props={{ size: 'medium', appearance: 'subtle' }}
						onOpenChange={onTreeSelectionChange}
						loadMoreNodes={loadMoreNodes}
						loadChildrens={loadChildrens}
						defaultOpenItems={defaultOpenItems ? [...defaultOpenItems] : []}
						treeItems={treeItems}
						defaultSelectedItems={defaultSelectedTableItems}
					/>
				) : status.status === STATUS.LOADING ? (
					<DQTreeSkeleton />
				) : (
					<Button appearance="primary" icon={<ArrowClockwiseRegular />}>
						Retry
					</Button>
				)}
			</DrawerBody>
		</InlineDrawer>
	);

	const collapsedExplorer = (
		<div
			className={mergeClasses(
				commonFlexStyles.flexContainer,
				commonFlexStyles.flexDirectionColumn,
				commonFlexStyles.flexAlignItemsCenter,
				style.backgroundColor,
				commonSpacingStyles.height100,
			)}
		>
			<Button appearance="transparent" icon={<ChevronDoubleRight16Filled />} onClick={() => setOpen(true)} />
			<div className={mergeClasses(style.explorerCollapsed, style.transform270Deg)}>Explorer</div>
		</div>
	);

	if (enableExpandablePanel) {
		// With FluentPanel wrapper
		return (
			<FluentPanel maxSize={maxSize} minSize={minSize}>
				{open ? expandedExplorer : collapsedExplorer}
			</FluentPanel>
		);
	} else {
		// Without FluentPanel wrapper
		return open ? expandedExplorer : collapsedExplorer;
	}
};
